import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, Camera, CheckCircle2, XCircle, User, Ticket, Calendar,
  AlertCircle, RefreshCw, Search, UserPlus, Clock, Shield, 
  CameraOff, Loader2, Hash, Mail, Users, CreditCard, Fingerprint
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import ProducerLayout from '../../components/producer/ProducerLayout';

const ProducerScannerPage = () => {
  const [scanMode, setScanMode] = useState('dni'); // 'dni', 'qr', or 'invitation'
  const [scanning, setScanning] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [cameraPermission, setCameraPermission] = useState('prompt');
  const [scanHistory, setScanHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, checkedIn: 0, pending: 0 });
  const html5QrcodeRef = useRef(null);
  const lastScanRef = useRef({ code: null, time: 0 });
  const inputRef = useRef(null);

  useEffect(() => {
    loadEvents();
    checkCameraPermission();
    return () => { stopScanner(); };
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      loadEventStats();
      // Refresh stats every 10 seconds for real-time updates
      const interval = setInterval(loadEventStats, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedEvent]);

  const checkCameraPermission = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'camera' });
      setCameraPermission(result.state);
      result.addEventListener('change', () => setCameraPermission(result.state));
    } catch (error) {
      setCameraPermission('prompt');
    }
  };

  const loadEvents = async () => {
    try {
      // Check if user is a validator
      const producerData = JSON.parse(localStorage.getItem('producer_data') || '{}');
      const isValidator = producerData.is_validator === true;
      
      if (isValidator && producerData.assigned_events) {
        // Validators see only their assigned events
        const assignedEvents = producerData.assigned_events || [];
        setEvents(assignedEvents);
        if (assignedEvents.length > 0) setSelectedEvent(assignedEvents[0]);
      } else {
        // Producers see all their events
        const response = await api.get('/producers/me/events');
        const activeEvents = (response.data || []).filter(e => 
          ['published', 'draft', 'pending_approval'].includes(e.status)
        );
        setEvents(activeEvents);
        if (activeEvents.length > 0) setSelectedEvent(activeEvents[0]);
      }
    } catch (error) {
      toast.error('Error al cargar eventos');
    }
  };

  const loadEventStats = async () => {
    if (!selectedEvent) return;
    try {
      // Check if user is a validator
      const producerData = JSON.parse(localStorage.getItem('producer_data') || '{}');
      const isValidator = producerData.is_validator === true;
      
      // Use the appropriate endpoint based on user type
      const endpoint = isValidator 
        ? `/producers/validators/events/${selectedEvent.id}/scanner-stats`
        : `/producers/me/events/${selectedEvent.id}/scanner-stats`;
      
      const response = await api.get(endpoint);
      const data = response.data;
      
      setStats({
        total: data.total || 0,
        checkedIn: data.checked_in || 0,
        pending: data.pending || 0,
        byDni: data.tickets?.by_dni || 0,
        byQr: data.tickets?.by_qr || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const startScanner = async () => {
    if (!selectedEvent) {
      toast.error('Selecciona un evento primero');
      return;
    }

    setCameraLoading(true);
    
    try {
      // Make sure the container is visible before starting
      const readerElement = document.getElementById('qr-reader');
      if (readerElement) {
        readerElement.style.display = 'block';
        readerElement.innerHTML = ''; // Clear any previous content
      }
      
      const html5Qrcode = new Html5Qrcode("qr-reader");
      html5QrcodeRef.current = html5Qrcode;
      
      const config = { 
        fps: 10, 
        qrbox: { width: 220, height: 220 },
        aspectRatio: 1.0,
        disableFlip: false,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      };
      
      await html5Qrcode.start(
        { facingMode: "environment" },
        config,
        onScanSuccess,
        () => {} // Ignore errors during scanning
      );
      
      setScanning(true);
      setCameraPermission('granted');
      setCameraLoading(false);
      toast.success('Cámara activada');
    } catch (error) {
      console.error('Camera error:', error);
      setCameraLoading(false);
      if (error.toString().includes('Permission') || error.toString().includes('denied')) {
        setCameraPermission('denied');
        toast.error('Permiso de cámara denegado. Habilitá la cámara en la configuración del navegador.');
      } else if (error.toString().includes('NotFound') || error.toString().includes('not found')) {
        toast.error('No se encontró ninguna cámara en el dispositivo');
      } else {
        toast.error('Error al iniciar cámara: ' + error.message);
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current = null;
      } catch (error) {}
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    const now = Date.now();
    if (decodedText === lastScanRef.current.code && now - lastScanRef.current.time < 4000) return;
    if (now - lastScanRef.current.time < 1200) return;
    lastScanRef.current = { code: decodedText, time: now };
    if (navigator.vibrate) navigator.vibrate(100);
    try { html5QrcodeRef.current?.pause(true); } catch (e) {}
    await verifyCode(decodedText, 'qr');
    setTimeout(() => {
      try { html5QrcodeRef.current?.resume(); } catch (e) {}
    }, 1600);
  };

  const verifyCode = async (code, method = scanMode) => {
    if (!selectedEvent) {
      toast.error('Selecciona un evento primero');
      return;
    }

    const codeClean = code.trim();
    if (!codeClean) return;

    setVerifying(true);
    setLastResult(null);

    // Check if user is a validator
    const producerData = JSON.parse(localStorage.getItem('producer_data') || '{}');
    const isValidator = producerData.is_validator === true;

    try {
      let response;
      let codeType = method === 'dni' ? 'dni' : 'ticket';

      // Check if it's an invitation code or invitation mode
      if (method === 'invitation' || codeClean.toUpperCase().startsWith('INV-')) {
        codeType = 'invitation';
        const invCode = codeClean.toUpperCase().startsWith('INV-') 
          ? codeClean.toUpperCase() 
          : `INV-${codeClean.toUpperCase()}`;
        response = await api.post(`/producers/me/events/${selectedEvent.id}/invitations/validate`, {
          code: invCode
        });
      } else {
        // Ticket or DNI
        let ticketCode = codeClean;
        let verifyMethod = method;
        
        // Parse QR format: BRAVO|DNI|ticket_code|event_id
        if (codeClean.startsWith('BRAVO|')) {
          const parts = codeClean.split('|');
          if (parts.length >= 3) {
            // Format: BRAVO|DNI|ticket_code|event_id
            ticketCode = parts[2]; // Extract ticket_code
            verifyMethod = 'qr';
          }
        } else if (method === 'qr' && !codeClean.toUpperCase().startsWith('BP-')) {
          // If manually entering QR code without BP- prefix, add it
          ticketCode = `BP-${codeClean.toUpperCase()}`;
        } else if (method === 'qr') {
          // Ensure uppercase for ticket codes
          ticketCode = codeClean.toUpperCase();
        }
        
        // Use the appropriate endpoint based on user type
        if (isValidator) {
          // Validators use their own endpoint
          response = await api.post('/producers/validators/verify-ticket', {
            event_id: selectedEvent.id,
            ticket_code: ticketCode,
            method: verifyMethod
          });
        } else {
          // Producers use their endpoint
          response = await api.post('/producers/me/verify-ticket', {
            event_id: selectedEvent.id,
            ticket_code: ticketCode,
            method: verifyMethod
          });
        }
      }

      const resultData = {
        success: response.data.success !== false,
        type: codeType,
        method: method,
        code: codeClean,
        message: response.data.message,
        alreadyUsed: response.data.already_checked_in,
        usedMethod: response.data.checked_in_method,
        data: response.data,
        timestamp: new Date()
      };

      setLastResult(resultData);
      setScanHistory(prev => [resultData, ...prev.slice(0, 19)]);
      loadEventStats();

      if (resultData.success) {
        toast.success(resultData.message);
      } else {
        toast.error(resultData.message);
      }

    } catch (error) {
      const detail = error.response?.data?.detail || 'Código no encontrado';
      const resultData = {
        success: false,
        type: 'unknown',
        method: method,
        code: codeClean,
        message: detail,
        timestamp: new Date()
      };
      
      setLastResult(resultData);
      setScanHistory(prev => [resultData, ...prev.slice(0, 19)]);
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    await verifyCode(inputValue.trim(), scanMode);
    setInputValue('');
  };

  const resetAndContinue = () => {
    setLastResult(null);
    if (scanMode === 'qr') {
      if (!scanning) startScanner();
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Auto-focus for invitation mode too
  useEffect(() => {
    if ((scanMode === 'dni' || scanMode === 'invitation') && inputRef.current && !lastResult) {
      inputRef.current.focus();
    }
  }, [scanMode, lastResult]);

  return (
    <ProducerLayout currentPage="scanner">
      <div className="p-4 lg:p-6 overflow-x-hidden" data-testid="producer-scanner-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-500/20 rounded-2xl mb-4">
              <Fingerprint size={32} className="text-violet-500" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-black text-white">
              Control de Acceso
            </h1>
            <p className="text-zinc-400 mt-2">Validá entradas por DNI o código QR</p>
          </div>

          {/* Mode Selector */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 mb-6 grid grid-cols-3 gap-2">
            <button
              onClick={() => { stopScanner(); setScanMode('dni'); }}
              className={`flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-lg font-bold transition-all ${
                scanMode === 'dni'
                  ? 'bg-violet-500 text-black'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <CreditCard size={20} />
              <span className="text-sm">DNI</span>
              <span className="text-[10px] opacity-70">Principal</span>
            </button>
            <button
              onClick={() => { stopScanner(); setScanMode('qr'); }}
              className={`flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-lg font-bold transition-all ${
                scanMode === 'qr'
                  ? 'bg-violet-500 text-black'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <QrCode size={20} />
              <span className="text-sm">QR / Ticket</span>
              <span className="text-[10px] opacity-70">Alternativo</span>
            </button>
            <button
              onClick={() => { stopScanner(); setScanMode('invitation'); }}
              className={`flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-lg font-bold transition-all ${
                scanMode === 'invitation'
                  ? 'bg-purple-500 text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <UserPlus size={20} />
              <span className="text-sm">Invitación</span>
              <span className="text-[10px] opacity-70">Cortesías</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Input/Scanner */}
            <div className="space-y-6">
              {/* Event Selector */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <label className="text-sm text-zinc-400 block mb-2 font-medium">
                  <Calendar size={14} className="inline mr-2" />
                  Evento
                </label>
                <select
                  value={selectedEvent?.id || ''}
                  onChange={(e) => setSelectedEvent(events.find(ev => ev.id === e.target.value))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-violet-500/50"
                >
                  <option value="">Seleccionar evento...</option>
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title} - {new Date(event.date).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                    </option>
                  ))}
                </select>
                
                {/* Stats */}
                {selectedEvent && (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-white">{stats.total}</p>
                        <p className="text-xs text-zinc-500">Total</p>
                      </div>
                      <div className="bg-green-500/10 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-green-400">{stats.checkedIn}</p>
                        <p className="text-xs text-zinc-500">Ingresados</p>
                      </div>
                      <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-yellow-400">{stats.pending}</p>
                        <p className="text-xs text-zinc-500">Pendientes</p>
                      </div>
                    </div>
                    {/* Check-in method breakdown */}
                    {(stats.byDni > 0 || stats.byQr > 0) && (
                      <div className="flex gap-2 justify-center text-xs">
                        <span className="bg-zinc-800 px-2 py-1 rounded flex items-center gap-1">
                          <CreditCard size={12} className="text-violet-500" />
                          <span className="text-zinc-400">DNI:</span>
                          <span className="text-white font-bold">{stats.byDni || 0}</span>
                        </span>
                        <span className="bg-zinc-800 px-2 py-1 rounded flex items-center gap-1">
                          <QrCode size={12} className="text-violet-500" />
                          <span className="text-zinc-400">QR:</span>
                          <span className="text-white font-bold">{stats.byQr || 0}</span>
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* DNI Mode */}
              {scanMode === 'dni' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
                >
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-violet-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <CreditCard size={40} className="text-violet-500" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Ingresá el DNI</h3>
                    <p className="text-zinc-500 text-sm mt-1">
                      El método principal de acceso es el DNI del titular
                    </p>
                  </div>
                  
                  <form onSubmit={handleSubmit}>
                    <Input
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value.replace(/[^0-9.]/g, ''))}
                      placeholder="Ej: 12.345.678"
                      className="bg-zinc-800 border-zinc-700 text-center text-2xl font-mono h-16 mb-4"
                      disabled={verifying}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      disabled={verifying || !inputValue.trim() || !selectedEvent}
                      className="w-full bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold h-14 text-lg"
                    >
                      {verifying ? (
                        <Loader2 className="animate-spin mr-2" size={24} />
                      ) : (
                        <Search size={24} className="mr-2" />
                      )}
                      Validar DNI
                    </Button>
                  </form>
                  
                  <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-xs text-zinc-400 text-center">
                      <Shield size={12} className="inline mr-1" />
                      Una vez validado por DNI, el código QR quedará inhabilitado
                    </p>
                  </div>
                </motion.div>
              )}

              {/* QR Mode */}
              {scanMode === 'qr' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Camera Scanner */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                      <h3 className="font-bold text-white flex items-center gap-2">
                        <Camera size={18} className="text-violet-500" />
                        Escáner QR
                      </h3>
                      {cameraPermission === 'denied' && (
                        <span className="text-xs text-red-400 flex items-center gap-1">
                          <CameraOff size={12} /> Sin permiso
                        </span>
                      )}
                    </div>
                    
                    {/* QR Reader Container - Always mounted but visibility controlled */}
                    <div 
                      id="qr-reader" 
                      className="qr-scanner-container"
                      style={{ 
                        width: '100%', 
                        minHeight: scanning ? '350px' : '0px',
                        maxHeight: '450px',
                        display: scanning ? 'block' : 'none',
                        backgroundColor: '#000',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    />
                    
                    {!scanning && !cameraLoading && (
                      <div className="aspect-square flex flex-col items-center justify-center p-8 bg-zinc-950">
                        <div className="relative mb-6">
                          <div className="w-32 h-32 border-2 border-dashed border-zinc-700 rounded-2xl flex items-center justify-center">
                            <QrCode size={48} className="text-zinc-700" />
                          </div>
                          <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-violet-500 rounded-tl-lg" />
                          <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-violet-500 rounded-tr-lg" />
                          <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-violet-500 rounded-bl-lg" />
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-violet-500 rounded-br-lg" />
                        </div>
                        <Button
                          onClick={startScanner}
                          disabled={!selectedEvent}
                          className="bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold px-8"
                        >
                          <Camera size={18} className="mr-2" />
                          Iniciar Cámara
                        </Button>
                        {cameraPermission === 'denied' && (
                          <p className="text-red-400 text-xs mt-4 text-center max-w-xs">
                            Permiso denegado. Habilitá la cámara en la configuración del navegador.
                          </p>
                        )}
                      </div>
                    )}
                    
                    {cameraLoading && (
                      <div className="aspect-square flex flex-col items-center justify-center p-8 bg-zinc-950">
                        <Loader2 size={48} className="text-violet-500 animate-spin mb-4" />
                        <p className="text-zinc-400 text-sm">Iniciando cámara...</p>
                      </div>
                    )}
                    
                    {scanning && (
                      <div className="p-4 border-t border-zinc-800">
                        <Button
                          onClick={stopScanner}
                          variant="outline"
                          className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <CameraOff size={16} className="mr-2" />
                          Detener Cámara
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Manual Code Input */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                    <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                      <Hash size={16} className="text-zinc-500" />
                      O ingresá el código manualmente
                    </h4>
                    <form onSubmit={handleSubmit} className="flex gap-2">
                      {scanMode === 'qr' ? (
                        <div className="flex-1 flex items-stretch bg-zinc-800 border-2 border-violet-500 rounded-lg overflow-hidden">
                          <div className="w-14 sm:w-16 flex items-center justify-center bg-violet-500 text-black font-mono font-bold text-sm sm:text-base flex-shrink-0">
                            BP-
                          </div>
                          <input
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                            placeholder="0B799478"
                            className="flex-1 min-w-0 bg-zinc-900 px-2 sm:px-3 py-2 font-mono text-sm sm:text-base text-white outline-none placeholder:text-zinc-600 uppercase"
                            disabled={verifying}
                          />
                        </div>
                      ) : (
                        <Input
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value.replace(/\D/g, ''))}
                          placeholder="DNI sin puntos"
                          className="bg-zinc-800 border-zinc-700 font-mono flex-1"
                          disabled={verifying}
                        />
                      )}
                      <Button
                        type="submit"
                        disabled={verifying || !inputValue.trim() || !selectedEvent}
                        className="bg-gradient-to-r from-violet-600 to-pink-600 text-white px-6"
                      >
                        {verifying ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
                      </Button>
                    </form>
                  </div>
                </motion.div>
              )}

              {/* Invitation Mode */}
              {scanMode === 'invitation' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
                >
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <UserPlus size={40} className="text-purple-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Validar Invitación</h3>
                    <p className="text-zinc-500 text-sm mt-1">
                      Ingresá el código de invitación (cortesía)
                    </p>
                  </div>
                  
                  <form onSubmit={handleSubmit}>
                    <div className="flex items-stretch bg-zinc-800 border-2 border-purple-400 rounded-lg overflow-hidden h-14 sm:h-16 mb-4">
                      <div className="w-16 sm:w-20 flex items-center justify-center bg-purple-500 text-white font-mono font-bold text-base sm:text-xl flex-shrink-0">
                        INV-
                      </div>
                      <input
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        placeholder="ABC123"
                        className="flex-1 min-w-0 bg-zinc-900 px-2 sm:px-4 text-base sm:text-xl font-mono text-white outline-none text-center placeholder:text-zinc-600 uppercase"
                        disabled={verifying}
                        autoFocus
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={verifying || !inputValue.trim() || !selectedEvent}
                      className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold h-14 text-lg"
                    >
                      {verifying ? (
                        <Loader2 className="animate-spin mr-2" size={24} />
                      ) : (
                        <Search size={24} className="mr-2" />
                      )}
                      Validar Invitación
                    </Button>
                  </form>
                  
                  <div className="mt-4 p-3 bg-purple-500/10 rounded-lg">
                    <p className="text-xs text-purple-400 text-center">
                      <UserPlus size={12} className="inline mr-1" />
                      Los códigos de invitación empiezan con INV-
                    </p>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right Column - Results */}
            <div className="space-y-6">
              {/* Last Result */}
              <AnimatePresence mode="wait">
                {lastResult ? (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className={`rounded-xl overflow-hidden ${
                      lastResult.success 
                        ? 'bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30' 
                        : 'bg-gradient-to-br from-red-500/20 to-red-500/5 border border-red-500/30'
                    }`}
                  >
                    <div className={`p-6 text-center ${lastResult.success ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.5 }}>
                        {lastResult.success ? (
                          <CheckCircle2 size={64} className="text-green-400 mx-auto mb-3" />
                        ) : (
                          <XCircle size={64} className="text-red-400 mx-auto mb-3" />
                        )}
                      </motion.div>
                      <h3 className={`text-2xl font-black ${lastResult.success ? 'text-green-400' : 'text-red-400'}`}>
                        {lastResult.success ? '¡ACCESO PERMITIDO!' : 'ACCESO DENEGADO'}
                      </h3>
                      <p className="text-zinc-400 mt-1">{lastResult.message}</p>
                    </div>
                    
                    <div className="p-4 space-y-3">
                      {/* Method Badge */}
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          lastResult.type === 'invitation' 
                            ? 'bg-purple-500/20 text-purple-400'
                            : lastResult.method === 'dni'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-violet-500/20 text-violet-500'
                        }`}>
                          {lastResult.type === 'invitation' ? (
                            <><UserPlus size={12} className="inline mr-1" /> INVITACIÓN</>
                          ) : lastResult.method === 'dni' ? (
                            <><CreditCard size={12} className="inline mr-1" /> DNI</>
                          ) : (
                            <><QrCode size={12} className="inline mr-1" /> QR</>
                          )}
                        </span>
                        {lastResult.alreadyUsed && (
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-500/20 text-red-400">
                            Ya validado por {lastResult.usedMethod?.toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* Attendee Info */}
                      {lastResult.data && (
                        <div className="bg-zinc-900/50 rounded-lg p-4 space-y-2">
                          {(lastResult.data.attendee_name || lastResult.data.name) && (
                            <div className="flex items-center gap-3">
                              <User size={16} className="text-zinc-500" />
                              <span className="text-white font-medium">
                                {lastResult.data.attendee_name || lastResult.data.name}
                              </span>
                            </div>
                          )}
                          {lastResult.data.attendee_dni && (
                            <div className="flex items-center gap-3">
                              <CreditCard size={16} className="text-zinc-500" />
                              <span className="text-zinc-400 text-sm">DNI: {lastResult.data.attendee_dni}</span>
                            </div>
                          )}
                          {lastResult.data.email && (
                            <div className="flex items-center gap-3">
                              <Mail size={16} className="text-zinc-500" />
                              <span className="text-zinc-400 text-sm">{lastResult.data.email}</span>
                            </div>
                          )}
                          {lastResult.data.ticket_type && (
                            <div className="flex items-center gap-3">
                              <Ticket size={16} className="text-zinc-500" />
                              <span className="text-zinc-400 text-sm">{lastResult.data.ticket_type}</span>
                            </div>
                          )}
                          {lastResult.data.quantity && (
                            <div className="flex items-center gap-3">
                              <Users size={16} className="text-zinc-500" />
                              <span className="text-zinc-400 text-sm">{lastResult.data.quantity} entrada(s)</span>
                            </div>
                          )}
                        </div>
                      )}

                      <Button onClick={resetAndContinue} className="w-full bg-zinc-800 hover:bg-zinc-700 text-white mt-4">
                        <RefreshCw size={16} className="mr-2" />
                        Siguiente
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="waiting"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center"
                  >
                    <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Shield size={32} className="text-zinc-600" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">Esperando validación</h3>
                    <p className="text-zinc-500 text-sm">
                      {scanMode === 'dni' 
                        ? 'Ingresá el DNI del asistente' 
                        : scanMode === 'invitation'
                        ? 'Ingresá el código de invitación'
                        : 'Escaneá el código QR o ingresalo manualmente'}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Security Notice */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Shield className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-blue-400 font-medium text-sm">Sistema anti-duplicación</p>
                    <p className="text-blue-400/70 text-xs mt-1">
                      Cada entrada puede validarse solo una vez. Si se usa el DNI, el QR queda inhabilitado y viceversa.
                    </p>
                  </div>
                </div>
              </div>

              {/* Scan History */}
              {scanHistory.length > 0 && (
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-zinc-800">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <Clock size={16} className="text-zinc-500" />
                      Últimas validaciones
                    </h3>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {scanHistory.slice(0, 10).map((scan, i) => (
                      <div 
                        key={i}
                        className={`p-3 border-b border-zinc-800/50 last:border-0 flex items-center gap-3 ${
                          scan.success ? 'bg-green-500/5' : 'bg-red-500/5'
                        }`}
                      >
                        {scan.success ? (
                          <CheckCircle2 size={16} className="text-green-400 flex-shrink-0" />
                        ) : (
                          <XCircle size={16} className="text-red-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">
                            {scan.data?.attendee_name || scan.data?.name || scan.code}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {scan.method === 'dni' ? 'DNI' : scan.type === 'invitation' ? 'Invitación' : 'QR'} • {scan.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ProducerLayout>
  );
};

export default ProducerScannerPage;
